import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import styled from "@emotion/styled";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import Button from "components/ui/button/Button";
import { BidStageSelect } from "components/ui/form_components/StageSelect/BidStageSelect";
import toast from "components/ui/toast/Toast";
import { useCreateBid } from "lib/hooks/api/bid/bids/useCreateBid";
import { useCreateCustomOrganisation } from "lib/hooks/api/customOrganisations/useCreateCustomOrganisation";
import { CustomOrganisationPrimaryRole } from "lib/types/graphQLEnums";
import { useBidWorkflow } from "../../../lib/hooks/api/bid/bids/useBidWorkflow";
import { routes } from "../../../lib/routes";
import { EventData } from "../../../lib/tracking";
import { BidEntityType } from "../../../lib/types/graphQLEnums";
import { Flex, Text } from "../../../styles/utility-components";
import { ToastAction } from "../../entities/ToastAction";
import { isBuyerOrganisation } from "../../entities/utils";
import { BuyersCombobox } from "../../ui/form_components/BidEntityCombobox/BuyersCombobox";
import { CompanyUserCombobox } from "../../ui/form_components/CompanyUserCombobox";
import Input, { StyledInput } from "../../ui/input/Input";
import Label from "../../ui/input/Label";
import { createBidSchema } from "../utils";

export type CreateBidFormInput = z.input<typeof createBidSchema> & {
  stage?: string;
};
export type CreateBidFormOutput = z.output<typeof createBidSchema> & {
  stage?: string;
};

type Props = {
  trackingData: EventData;
  defaultValues?: Partial<CreateBidFormOutput>;
  onComplete?: () => void;
  navigateToNewBid?: boolean;
};

export function CreateBidForm({
  trackingData,
  defaultValues,
  onComplete,
  navigateToNewBid,
}: Props) {
  const navigate = useNavigate();
  // Metadata lookup to enrich custom buyer IDs with their names
  // in the combobox and their entity type at submit time
  const [customBuyers, setCustomBuyers] = useState<{ id: string; name: string }[]>([]);

  const defaultBuyerIds = useMemo(
    () => defaultValues?.entities?.filter(isBuyerOrganisation)?.map((e) => e.entityId) ?? [],
    [defaultValues?.entities],
  );

  const { formState, control, handleSubmit, reset, setValue, watch, getValues } = useForm<
    CreateBidFormInput,
    unknown,
    CreateBidFormOutput
  >({
    mode: "onChange",
    defaultValues: {
      ...defaultValues,
      entities: defaultValues?.entities ?? [],
      buyerIds: defaultBuyerIds,
    },
    resolver: zodResolver(createBidSchema),
  });

  const stageId = watch("stageId");

  const { mutate: createBid, isLoading } = useCreateBid(trackingData, {
    onSuccess: (data) => {
      reset(defaultValues);
      setCustomBuyers([]);
      const id = data.createBid.id;
      if (navigateToNewBid) {
        void navigate({ to: "/bids/$id", params: { id } });
        return;
      }
      onComplete?.();
      toast.message("Bid created successfully", {
        action: <ToastAction objectType="bid" url={`${routes.bids}/${id}`} />,
      });
    },
    onError: () => toast.error("Failed to create bid"),
  });

  const { mutate: createCustomOrganisation } = useCreateCustomOrganisation({
    onSuccess: (data) => {
      const newCustomBuyer = data.createCustomOrganisation;
      const currentBuyerIds = getValues("buyerIds");
      setValue("buyerIds", [...(currentBuyerIds ?? []), newCustomBuyer.id], {
        shouldValidate: true,
      });
      setCustomBuyers((prev) => [...prev, newCustomBuyer]);
    },
    onError: (error) => {
      toast.error("Failed to add custom buyer");
      console.error("Failed to create custom buyer:", error);
    },
  });

  // By default, set the stage to the first stage in the workflow
  const { data: workflow } = useBidWorkflow({}, { enabled: !!defaultValues?.stageId });

  useEffect(() => {
    if (workflow && !stageId) {
      const defaultStage = workflow.transitions[0].stage.id;
      if (defaultStage) {
        setValue("stageId", defaultStage, { shouldValidate: true });
      }
    }
  }, [workflow, stageId, setValue, defaultValues]);

  return (
    <Form
      onSubmit={handleSubmit(({ entities: _entities, buyerIds, ...bid }) => {
        const customBuyerIdSet = new Set(customBuyers.map((b) => b.id));

        const buyerEntities = (buyerIds ?? []).map((id) => ({
          entityId: id,
          entityType: customBuyerIdSet.has(id)
            ? BidEntityType.CustomOrganisation
            : BidEntityType.Buyer,
        }));

        const nonBuyerEntities = (defaultValues?.entities ?? []).filter(
          (e) => !isBuyerOrganisation(e) && e.entityType !== BidEntityType.CustomOrganisation,
        );

        createBid({
          bid,
          entities: {
            entities: [...nonBuyerEntities, ...buyerEntities],
          },
        });
      })}
    >
      <Flex flexDirection="column" gap={24}>
        <div>
          <Label htmlFor="bid-name" required>
            Name
          </Label>
          <Input name="name" control={control} required placeholder="Name this bid" id="bid-name" />
        </div>
        <div>
          <Label htmlFor="buyer-ids">Buyer</Label>
          <BuyersCombobox
            control={control}
            name="buyerIds"
            trigger={<StyledInput id="buyer-ids" placeholder="Select buyers" />}
            customBuyers={customBuyers}
            onAddCustomBuyer={(name) =>
              createCustomOrganisation({
                input: {
                  name,
                  primaryRole: CustomOrganisationPrimaryRole.Buyer,
                },
              })
            }
          />
        </div>
        <Flex flexDirection="column" gap={4} aria-label="Stage field">
          <Text>Stage</Text>
          <BidStageSelect name="stageId" control={control} />
        </Flex>
        <Flex flexDirection="column" gap={4}>
          <Text>Assigned To</Text>
          <CompanyUserCombobox control={control} name="assignedToId" placeholder="Assigned To" />
        </Flex>
      </Flex>
      <Flex column gap={8} flexGrow={1} justifyContent="flex-end">
        <Button
          buttonType="primary"
          type="submit"
          disabled={isLoading || !formState.isValid}
          loading={isLoading}
        >
          Create bid
        </Button>
      </Flex>
    </Form>
  );
}

const Form = styled.form({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  flexGrow: 1,
  gap: 24,
});
